import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

export interface ApiResponse<T = any> {
  status_code: number;
  error: boolean;
  message: string;
  result?: T;
  data?: T;
  session?: {
    id: string;
    cookieName: string;
    expiresAt: string;
    maxAge: number;
  };
  device_id?: string;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  credentials: 'include',
  prepareHeaders: (headers) => {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('ngrok-skip-browser-warning', 'true');
    headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
    headers.set('Pragma', 'no-cache');
    headers.set('Expires', '0');
    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const currentPath = window.location.pathname;

    if (!currentPath.startsWith('/auth')) {
      clearSessionMetadata();
      sessionStorage.setItem('redirect_after_login', currentPath);
      window.dispatchEvent(new CustomEvent('api:unauthorized', { detail: { path: currentPath } }));
    }
  }

  if (result.error?.status === 403) {
    const currentPath = window.location.pathname;
    if (!currentPath.startsWith('/auth') && !currentPath.includes('access-denied')) {
      window.location.href = '/app/dashboard?error=access_denied';
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Auth',
    'User',
    'Profile',
    'Device',
    'Activity',
    'Settings',
    'Project',
    'Storage',
    'Monitor',
    'Admins',
    'GatewayTypes',
    'DeviceTypes',
  ],
  endpoints: () => ({}),
});

export const setSessionMetadata = (userEmail: string, userData?: any): void => {
  sessionStorage.setItem('user_email', userEmail);
  if (userData) {
    sessionStorage.setItem('user_data', JSON.stringify(userData));
  }
};

export const clearSessionMetadata = (): void => {
  sessionStorage.removeItem('user_email');
  sessionStorage.removeItem('user_data');
  sessionStorage.clear();
};

export const getSessionMetadata = (): string | null => sessionStorage.getItem('user_email');

export const getUserData = (): any | null => {
  const data = sessionStorage.getItem('user_data');
  return data ? JSON.parse(data) : null;
};

export const hasSessionMetadata = (): boolean => !!sessionStorage.getItem('user_email');
